import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { backupAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Clock, Play, Check, AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { encryptBackup, hashRecoveryPhrase, decryptContent } from '@/lib/crypto';

const SCHEDULE_KEY = 'vaultnotes_backup_schedule';
const HISTORY_KEY = 'vaultnotes_backup_history';

function getScheduleSettings() {
  try { return JSON.parse(localStorage.getItem(SCHEDULE_KEY)) || null; } catch { return null; }
}

function saveScheduleSettings(s) {
  localStorage.setItem(SCHEDULE_KEY, JSON.stringify(s));
}

function getBackupHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY)) || []; } catch { return []; }
}

function addBackupHistory(entry) {
  const history = getBackupHistory();
  history.unshift(entry);
  // Keep last 20
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 20)));
}

function getIntervalMs(freq) {
  switch (freq) {
    case 'daily': return 24 * 60 * 60 * 1000;
    case 'weekly': return 7 * 24 * 60 * 60 * 1000;
    case 'monthly': return 30 * 24 * 60 * 60 * 1000;
    default: return 7 * 24 * 60 * 60 * 1000;
  }
}

export default function ScheduledBackupSettings() {
  const { hasRecoveryKey, user, encryptionKey, recordBackup } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState('weekly');
  const [retentionCount, setRetentionCount] = useState(5);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const s = getScheduleSettings();
    if (s) {
      setEnabled(s.enabled || false);
      setFrequency(s.frequency || 'weekly');
      setRetentionCount(s.retentionCount || 5);
    }
    setHistory(getBackupHistory());
  }, []);

  // Check if backup is due
  useEffect(() => {
    if (!enabled) return;
    const check = () => {
      const hist = getBackupHistory();
      const last = hist.find((h) => h.status === 'success');
      if (!last) return true; // Never backed up
      return (Date.now() - new Date(last.timestamp).getTime()) > getIntervalMs(frequency);
    };
    if (check()) {
      // Notify user backup is due
      toast.info('Scheduled backup is due. Click "Run Now" in Settings > Backup.', { duration: 8000 });
    }
  }, [enabled, frequency]);

  const saveSettings = useCallback((e, f, r) => {
    saveScheduleSettings({ enabled: e, frequency: f, retentionCount: r });
  }, []);

  const handleToggle = (val) => {
    setEnabled(val);
    saveSettings(val, frequency, retentionCount);
    if (val) toast.success('Scheduled backups enabled');
  };

  const handleFrequencyChange = (val) => {
    setFrequency(val);
    saveSettings(enabled, val, retentionCount);
  };

  const handleRetentionChange = (val) => {
    const n = parseInt(val) || 5;
    setRetentionCount(n);
    saveSettings(enabled, frequency, n);
  };

  const runBackupNow = async () => {
    if (!hasRecoveryKey) {
      toast.error('Set a recovery key first to enable encrypted backups');
      return;
    }
    setRunning(true);
    const entry = { timestamp: new Date().toISOString(), status: 'running', frequency };

    try {
      const res = await backupAPI.exportBundle({});
      let bundle = res.data;

      // Decrypt encrypted notes
      if (encryptionKey && bundle.data.notes) {
        const decrypted = await Promise.all(
          bundle.data.notes.map(async (note) => {
            if (note.is_encrypted && note.encryption_iv && note.content) {
              try {
                const dc = await decryptContent(note.content, note.encryption_iv, encryptionKey);
                return { ...note, content: dc, is_encrypted: false, encryption_iv: null };
              } catch { return note; }
            }
            return note;
          })
        );
        bundle = { ...bundle, data: { ...bundle.data, notes: decrypted } };
      }

      // Auto-download
      const key = prompt('Enter your 24-word recovery key to encrypt the scheduled backup:');
      if (!key?.trim()) {
        entry.status = 'cancelled';
        addBackupHistory(entry);
        setHistory(getBackupHistory());
        setRunning(false);
        return;
      }
      const normalizedKey = key.trim().toLowerCase().replace(/\s+/g, ' ');
      const keyHash = await hashRecoveryPhrase(normalizedKey);
      if (keyHash !== user.recovery_key_hash) {
        entry.status = 'failed';
        entry.error = 'Invalid recovery key';
        addBackupHistory(entry);
        setHistory(getBackupHistory());
        toast.error('Invalid recovery key');
        setRunning(false);
        return;
      }

      const encrypted = await encryptBackup(bundle, normalizedKey);
      const blob = new Blob([JSON.stringify(encrypted, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vaultnotes-scheduled-${new Date().toISOString().split('T')[0]}.encrypted.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      entry.status = 'success';
      entry.noteCount = bundle.manifest.counts.notes;
      addBackupHistory(entry);
      await recordBackup();
      toast.success(`Scheduled backup exported (${bundle.manifest.counts.notes} notes)`);
    } catch (err) {
      entry.status = 'failed';
      entry.error = err.message;
      addBackupHistory(entry);
      toast.error('Scheduled backup failed');
    }
    setHistory(getBackupHistory());
    setRunning(false);
  };

  const clearHistory = () => {
    localStorage.setItem(HISTORY_KEY, '[]');
    setHistory([]);
    toast.success('History cleared');
  };

  const lastSuccess = history.find((h) => h.status === 'success');
  const lastFailed = history.find((h) => h.status === 'failed');

  return (
    <div className="space-y-4" data-testid="scheduled-backup-settings">
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-accent-cyan" />
        <h3 className="text-sm font-semibold">Scheduled Backups</h3>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed">
        Configure automatic backup reminders. Backups are generated and downloaded to your device — no cloud dependency.
      </p>

      <div className="flex items-center justify-between">
        <Label className="text-xs">Enable scheduled backups</Label>
        <Switch checked={enabled} onCheckedChange={handleToggle} data-testid="schedule-enable-toggle" />
      </div>

      {enabled && (
        <div className="space-y-3 animate-fade-in">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Frequency</Label>
            <select value={frequency} onChange={(e) => handleFrequencyChange(e.target.value)} className="w-full h-8 px-2 text-xs bg-input border border-border rounded outline-none" data-testid="schedule-frequency">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Keep last N backups (retention)</Label>
            <select value={retentionCount} onChange={(e) => handleRetentionChange(e.target.value)} className="w-full h-8 px-2 text-xs bg-input border border-border rounded outline-none" data-testid="schedule-retention">
              <option value="3">3 backups</option>
              <option value="5">5 backups</option>
              <option value="10">10 backups</option>
              <option value="20">20 backups</option>
            </select>
          </div>

          <Button onClick={runBackupNow} size="sm" variant="outline" className="w-full" disabled={running || !hasRecoveryKey} data-testid="schedule-run-now">
            <Play className="w-3.5 h-3.5 mr-1.5" /> {running ? 'Running...' : 'Run Backup Now'}
          </Button>

          {!hasRecoveryKey && (
            <p className="text-xs text-muted-foreground/70 text-center">Set a recovery key first</p>
          )}
        </div>
      )}

      {/* Status & History */}
      {history.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">History</p>
            <button onClick={clearHistory} className="text-xs text-muted-foreground hover:text-foreground"><Trash2 className="w-3 h-3" /></button>
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {history.slice(0, 8).map((h) => (
              <div key={h.timestamp} className="flex items-center justify-between py-1.5 px-2 rounded text-xs bg-secondary/20" data-testid={`schedule-history-${h.timestamp}`}>
                <div className="flex items-center gap-1.5">
                  {h.status === 'success' && <Check className="w-3 h-3 text-green-500" />}
                  {h.status === 'failed' && <AlertTriangle className="w-3 h-3 text-red-500" />}
                  {h.status === 'cancelled' && <AlertTriangle className="w-3 h-3 text-muted-foreground" />}
                  <span className="text-muted-foreground">{new Date(h.timestamp).toLocaleDateString()}</span>
                </div>
                <span className={h.status === 'success' ? 'text-green-500' : h.status === 'failed' ? 'text-red-500' : 'text-muted-foreground'}>
                  {h.status === 'success' ? `${h.noteCount} notes` : h.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
